//File: src/contexts/AuthContext.jsx

/*
 * Critical Design
 * Do not modify this section without explicit permission
 *
 * This context provides centralized authentication state management.
 * Uses Firebase Auth for all authentication operations.
 * Loading state prevents auth-dependent components from rendering until auth state is determined.
 * All auth methods return promises to allow proper error handling in consuming components.
 */

import { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInAnonymously as firebaseSignInAnonymously,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  GoogleAuthProvider,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { app, db } from '../config/firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth(app);

  const signUp = async ({ email, password, firstName, lastName, phoneNumber }) => {
    try {
      console.log('Starting signup process for:', email);
      
      // Create the user in Firebase Auth
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      console.log('Firebase Auth account created successfully:', user.uid);
      
      // Create a user profile document in Firestore
      const userData = {
        firstName,
        lastName,
        phoneNumber,
        email,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(doc(db, 'users', user.uid), userData);
      console.log('Firestore profile created successfully');

      return user;
    } catch (error) {
      console.error('Error in signUp:', error.code, error.message);
      // Map Firebase error codes to user-friendly messages
      const errorMessage = {
        'auth/email-already-in-use': 'An account with this email already exists.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/operation-not-allowed': 'Email/password accounts are not enabled. Please contact support.',
        'auth/weak-password': 'Password should be at least 6 characters.'
      }[error.code] || error.message;
      
      throw new Error(errorMessage);
    }
  };

  const signInWithEmail = async (email, password) => {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    try {
      console.log('AuthContext: Attempting sign in with email');
      const result = await signInWithEmailAndPassword(auth, email, password);
      console.log('AuthContext: Sign in successful', result.user.uid);
      return result;
    } catch (error) {
      console.error('AuthContext: Sign in error:', error.code, error.message);
      throw error;
    }
  };

  const signInWithProvider = async (providerName) => {
    let provider;
    switch (providerName.toLowerCase()) {
      case 'google':
        provider = new GoogleAuthProvider();
        break;
      default:
        throw new Error('Unsupported provider');
    }

    try {
      const { user } = await signInWithPopup(auth, provider);
      
      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create user profile for social login
        const names = user.displayName?.split(' ') || ['', ''];
        await setDoc(doc(db, 'users', user.uid), {
          firstName: names[0],
          lastName: names.slice(1).join(' '),
          email: user.email,
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }

      return user;
    } catch (error) {
      console.error('Error in signInWithProvider:', error);
      throw error;
    }
  };

  const signInAnonymously = () => {
    return firebaseSignInAnonymously(auth);
  };

  const signOut = () => {
    return firebaseSignOut(auth);
  };

  const resetPassword = async (email) => {
    if (!email) {
      throw new Error('Email is required');
    }
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Error in resetPassword:', error);
      const errorMessage = {
        'auth/user-not-found': 'No account found with this email.',
        'auth/invalid-email': 'Please enter a valid email address.',
      }[error.code] || error.message;
      throw new Error(errorMessage);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get user profile data from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setCurrentUser({
            ...user,
            profile: userDoc.data()
          });
        } else {
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    signUp,
    signInWithEmail,
    signInWithProvider,
    signInAnonymously,
    signOut,
    resetPassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export default AuthProvider; 